import type { ScopeContext } from '../../features/auth/model/types';
import { DebugLogger } from './debug';

const SCOPE_STORAGE_KEY = 'veltiz-scope-context';

export class ScopeContextStorage {
  /**
   * Store the current scope context in localStorage
   */
  static storeScopeContext(sessionId: string, scopeContext: ScopeContext): void {
    try {
      const data = {
        sessionId,
        scopeContext,
        timestamp: Date.now()
      };
      
      localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(data));
      
      DebugLogger.scope('Scope context stored', { 
        sessionId, 
        scopeContext,
        timestamp: data.timestamp 
      });
    } catch (error) {
      DebugLogger.error('Failed to store scope context', { 
        sessionId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  }

  /**
   * Store the scope context only when no record exists for this session yet.
   *
   * The stored record carries two things: the session pointer (read by
   * getCurrentSessionId) and the user's scope selection. A backwards arrival must not
   * overwrite a selection that is already stored, but it must still establish the
   * pointer when nothing is stored — or when the stored record belongs to a previous
   * session, which would otherwise resolve to a stale session id.
   */
  static ensureScopeContext(sessionId: string, scopeContext: ScopeContext): void {
    if (ScopeContextStorage.getCurrentSessionId() === sessionId) {
      DebugLogger.scope('Scope context already stored for this session, keeping it', {
        sessionId
      });
      return;
    }

    ScopeContextStorage.storeScopeContext(sessionId, scopeContext);
  }

  /**
   * Retrieve the stored scope context for a session
   */
  static getScopeContext(sessionId: string): ScopeContext | null {
    try {
      const stored = localStorage.getItem(SCOPE_STORAGE_KEY);
      
      if (!stored) {
        DebugLogger.scope('No stored scope context found');
        return null;
      }

      const data = JSON.parse(stored);
      
      // Check if the stored context belongs to the current session
      if (data.sessionId !== sessionId) {
        DebugLogger.scope('Stored scope context belongs to different session', { 
          storedSessionId: data.sessionId, 
          currentSessionId: sessionId 
        });
        return null;
      }

      DebugLogger.scope('Scope context retrieved', { 
        sessionId, 
        scopeContext: data.scopeContext,
        timestamp: data.timestamp 
      });

      return data.scopeContext;
    } catch (error) {
      DebugLogger.error('Failed to retrieve scope context', { 
        sessionId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      return null;
    }
  }

  /**
   * Clear the stored scope context
   */
  static clearScopeContext(): void {
    try {
      localStorage.removeItem(SCOPE_STORAGE_KEY);
      DebugLogger.scope('Scope context cleared');
    } catch (error) {
      DebugLogger.error('Failed to clear scope context', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  }

  /**
   * Check if scope context is complete (has both account holder and account)
   */
  static isScopeComplete(scopeContext: ScopeContext): boolean {
    return !!(scopeContext.scopeId && scopeContext.scopeType);
  }

  /**
   * Check if account holder selection is needed
   */
  static needsAccountHolderSelection(scopeContext: ScopeContext): boolean {
    return !scopeContext.accountHolder;
  }

  /**
   * Check if account selection is needed
   */
  /**
   * Which half of the scope is still missing, or null when it is complete.
   *
   * The two predicates below only mean "complete" as a pair, and the pairing is easy to get
   * wrong in the same way twice: a direct-account user has no holder and still has a
   * complete scope, so the holder test only counts when there is no account either. Callers
   * that route on completeness ask this rather than re-deriving the combination.
   */
  static needsScopeSelection(scopeContext: ScopeContext): 'holder' | 'account' | null {
    if (ScopeContextStorage.needsAccountHolderSelection(scopeContext) && !scopeContext.account) {
      return 'holder';
    }
    if (ScopeContextStorage.needsAccountSelection(scopeContext)) return 'account';
    return null;
  }

  static needsAccountSelection(scopeContext: ScopeContext): boolean {
    return !!(scopeContext.accountHolder && !scopeContext.account);
  }

  /**
   * Get the current session ID from stored scope context
   */
  static getCurrentSessionId(): string | null {
    try {
      const stored = localStorage.getItem(SCOPE_STORAGE_KEY);
      
      if (!stored) {
        DebugLogger.scope('No stored scope context found');
        return null;
      }

      const data = JSON.parse(stored);
      return data.sessionId || null;
    } catch (error) {
      DebugLogger.error('Failed to get current session ID', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      return null;
    }
  }
}
